import { Mic, RecordVoiceOverRounded, Pause, Stop } from '@mui/icons-material';
import { Badge, Stack, Typography, Button, Box, LinearProgress, Grid, Card } from '@mui/material';
import { useScribeContext } from 'op-contexts';
import { ScribeStatus } from 'op-contexts';
import PatientConsentModal from './PatientConsentModal';
import { useHistory, useRouteMatch } from 'react-router-dom';
// import scribeGif from './scribe.gif'; // Replaced with CSS animation

/**
 * ScribeCard component with CSS animation indicator
 * - Uses CSS keyframes for smooth, controllable animation
 * - Animation speed can be easily adjusted via the duration property
 * - Includes proper accessibility attributes
 */
const ScribeCard = () => {
  // Animation configuration - adjust these values to change speed
  // Speed options:
  // - Very fast: '0.2s' duration, '0.04s' delay
  // - Fast: '0.4s' duration, '0.08s' delay (current)
  // - Normal: '0.6s' duration, '0.1s' delay
  // - Slow: '1.0s' duration, '0.15s' delay
  const ANIMATION_DURATION = '0.9s'; // Faster animation (was 0.6s)
  const ANIMATION_DELAY_STEP = '0.51s'; // Faster stagger (was 0.1s)
  const history = useHistory();
  const match = useRouteMatch<any>();
  const { id: patientId, oncologyType } = match.params;
  const {
    isScribing,
    timer,
    consentModalOpen,
    handleStartScribe,
    handleResumeScribe,
    handleConsentConfirm,
    handleConsentCancel,
    handleStopScribe,
    handlePause,
    formatTime,
  } = useScribeContext();

  return (
    <Card
      variant="outlined"
      elevation={0}
      sx={{
        overflow: 'auto',
        flexBasis: '33%',
        boxSizing: 'border-box',
        borderRadius: '8px',
        minWidth: '31%',
        width: 'fit-content',
        padding: '0 8px 8px',
        height: '97px',
        marginLeft: '8px',
      }}>
      <PatientConsentModal isOpen={consentModalOpen} onClose={handleConsentCancel} onConfirm={handleConsentConfirm} />
      <div style={{ padding: 8 }}>
        <Grid
          container
          spacing={1}
          sx={{ width: '100%', maxWidth: '100%', margin: 0, height: '100%', overflow: 'hidden' }}>
          <Grid xs={4}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
              <RecordVoiceOverRounded color="primary" />
              <Typography variant="body2">Scribe</Typography>
            </Stack>
          </Grid>
          <Grid xs={8}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: '#f0f0f0',
                padding: '0 12px',
                borderRadius: '20px',
                // minWidth: '120px',
                opacity: isScribing !== ScribeStatus.STOPPED ? 1 : 0,
                transition: 'opacity 0.5s ease',
              }}>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                {formatTime(timer)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                  overflow: 'hidden',
                  borderRadius: '4px',
                  gap: 0.5,
                }}>
                {[...Array(10)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: '3px',
                      height: '12px',
                      backgroundColor: 'primary.main',
                      borderRadius: '1px',
                      animation:
                        isScribing === ScribeStatus.STARTED
                          ? `scribePulse ${ANIMATION_DURATION} ease-in-out infinite`
                          : 'none',
                      animationDelay: `${i * parseFloat(ANIMATION_DELAY_STEP)}s`,
                      '@keyframes scribePulse': {
                        '0%, 100%': {
                          height: '8px',
                          opacity: 0.6,
                        },
                        '50%': {
                          height: '16px',
                          opacity: 1,
                        },
                      },
                    }}
                    data-testid={`scribe-bar-${i}`}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
          <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
            {isScribing !== ScribeStatus.STARTED && (
              <Button
                variant="contained"
                startIcon={<Mic sx={{ marginRight: 0 }} />}
                onClick={isScribing === ScribeStatus.PAUSED ? handleResumeScribe : handleStartScribe}
                data-testid="start-scribe-btn"
                aria-label="Start Scribe"
                sx={{
                  borderColor: 'primary.main',
                  borderRadius: '4px',
                  minWidth: '0px',
                  '& .MuiButton-startIcon': { marginRight: '0px' },
                }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: 'white' }}>
                  {isScribing === ScribeStatus.PAUSED ? 'RESUME' : 'START'}
                </Typography>
              </Button>
            )}
            {isScribing === ScribeStatus.STARTED && (
              <Button
                variant="outlined"
                startIcon={<Pause sx={{ marginRight: 0 }} />}
                onClick={handlePause}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderRadius: '4px',
                  minWidth: '0px',
                  '& .MuiButton-startIcon': { marginRight: '0px' },
                }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  PAUSE
                </Typography>
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Stop sx={{ color: 'error.main' }} />}
              onClick={() => {
                handleStopScribe();
                history.push(`/${oncologyType}/patient/${patientId}/scribe`);
              }}
              sx={{
                backgroundColor: 'primary.main',
                textTransform: 'none',
                fontWeight: 500,
                width: '100%',
                opacity: isScribing !== ScribeStatus.STOPPED ? 1 : 0,
                transition: 'opacity 0.5s ease',
                borderRadius: '4px',
              }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'white' }}>
                STOP SCRIBE
              </Typography>
            </Button>
          </Stack>
        </Grid>
      </div>
    </Card>
  );
};

export default ScribeCard;
